import React, { useState, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { RETURN_PATH_KEY } from '../../util/requireAuth';
import { startAuthentication } from '@simplewebauthn/browser';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/authContext';
import useUser from '../../hooks/useUser';
import useWebAuthnApi from '../../hooks/useWebAuthnApi';
import CardCheck from '../components/Login/CardCheck';
import { hashCardId } from '../../util/hashUtils';
import { getDeviceInstallId } from '../../util/deviceInfo';
import { Box } from '@mui/material';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

function RfidLogin() {
  const authCtx = useAuth();
  const returnPathRef = useRef(null);
  const [showPasswordLogin, setShowPasswordLogin] = useState(false);
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showNoTokenModal, setShowNoTokenModal] = useState(false);
  const [showFirstLoginModal, setShowFirstLoginModal] = useState(false);
  const [showCheckmarkAnimation, setShowCheckmarkAnimation] = useState(false);
  const [showCrossAnimation, setShowCrossAnimation] = useState(false);
  const { error: httpError, loginWithAuthHeaders } = useUser();
  const { webauthnLoginStart, webauthnLoginFinish } = useWebAuthnApi();

  // Get intermediate tokens
  const intermediateTokens = authCtx.intermediateTokens;
  const hasIntermediateTokens = Object.keys(intermediateTokens).length > 0;

  // Check if error is a 401 authentication error
  const isAuthError = httpError && httpError.includes('401');

  // If not on counter device (no device token) or no intermediate tokens, redirect to normal login
  const hasDeviceToken = !!authCtx.deviceToken;
  if (!hasDeviceToken || !hasIntermediateTokens) {
    return <Navigate to='/login' replace={true} />;
  }

  // If already logged in, restore the view the user was on before lock/session timeout.
  // Use ref so we keep the same path across re-renders (auth updates can trigger a second render before Navigate completes).
  if (authCtx.isLoggedIn) {
    if (returnPathRef.current === null) {
      const storedPath = sessionStorage.getItem(RETURN_PATH_KEY);
      const publicPaths = ['/', '/login', '/rfid-login', '/request-password-reset', '/reset-password'];
      returnPathRef.current =
        storedPath && storedPath.length > 1 && !publicPaths.includes(storedPath)
          ? storedPath
          : '/welcome';
      // Do not remove from sessionStorage here - React Strict Mode remounts components,
      // and a fresh instance would read null. RequireAuth overwrites the path when we land on a protected route.
    }
    return <Navigate to={returnPathRef.current} replace={true} />;
  }

  const handleRfidSuccess = async (cardId, intermediateTokenData) => {
    try {
      await authCtx.rfidLogin(cardId, intermediateTokenData);
    } catch (err) {
      // Check if it's a "no intermediate token" error
      if (
        err.code === 'NO_INTERMEDIATE_TOKEN' ||
        err.message?.includes('Intermediate token required')
      ) {
        setShowNoTokenModal(true);
        return;
      }
      // If token is invalid, remove it from intermediate tokens
      if (err.code === 'INVALID_INTERMEDIATE_TOKEN') {
        // We need to pass the hashed key to removeIntermediateToken
        const hashedInput = await hashCardId(cardId);
        authCtx.removeIntermediateToken(hashedInput);
      }
      // Continue to fallback method
      setError(err.message || 'Fehler beim RFID-Login');
    }
  };

  const handlePasswordLogin = async (event, username, password) => {
    if (event) {
      event.preventDefault();
    }
    setError('');

    try {
      let loginResult = await loginWithAuthHeaders(username, password, authCtx.isPrivateDevice);

      if (loginResult && loginResult.needsDeviceConfirmation) {
        //should not happen on counter devices, but handle it just in case
        setError('Gerätebestätigung erforderlich. Bitte kontaktiere den Administrator.');
      } else if (loginResult) {
        // For direct login (no device confirmation needed)
        // Device tokens are only created via the device-token endpoint, not via login
        await authCtx.login(loginResult.token, loginResult);
      }
    } catch (error) {
      setError(error.message || 'Fehler beim Login');
    }
  };

  const handlePasskeyLogin = async () => {
    setError('');
    setIsProcessing(true);
    try {
      const startResponse = await webauthnLoginStart();
      const assertion = await startAuthentication({ optionsJSON: startResponse.options });
      const loginResult = await webauthnLoginFinish({
        challenge: startResponse.challenge,
        credential: assertion,
        isPrivateDevice: false,
        // Identifies this browser profile so an approved device type request is
        // activated here too, not only on the standard login screen.
        installId: getDeviceInstallId(),
      });

      if (loginResult?.needsDeviceConfirmation) {
        setError('Gerätebestätigung erforderlich. Bitte kontaktiere den Administrator.');
        return;
      }

      await authCtx.login(loginResult.token, loginResult);
      toast.success('Passkey-Anmeldung erfolgreich.');
    } catch (error) {
      setError(error?.message || 'Fehler bei der Passkey-Anmeldung');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Box sx={viewRootTextPrimarySx}>
    <CardCheck
      // Core props
      isOpen={true}
      onClose={() => {}} // No close functionality for this component
      showCloseButton={false} // Don't show close button for RfidLogin
      // RFID handling
      onRfidSuccess={handleRfidSuccess}
      intermediateTokens={intermediateTokens}
      // Password handling
      onPasswordSuccess={handlePasswordLogin}
      // UI customization
      title={showPasswordLogin ? 'Passwort-Login' : 'Mitarbeiterausweis-Login'}
      description={
        showPasswordLogin
          ? 'Gib deine Anmeldedaten ein, um dich anzumelden.'
          : 'Falls es dein erster Login heute ist, mussst du dich mit deinen Anmeldedaten anmelden.'
      }
      showPasswordLogin={showPasswordLogin}
      setShowPasswordLogin={setShowPasswordLogin}
      showNoEligibleUsersMessage={false} // Don't show message for normal login
      disableButtonsWhenNoEligibleUsers={false} // Don't disable buttons for normal login
      // Styling
      darkTheme={false}
      showLogo={true}
      // Button customization
      authenticateButtonText='Anmelden'
      showBackButton={false}
      showPasskeyLoginButton={true}
      onPasskeyLogin={handlePasskeyLogin}
      passkeyLoginButtonText='Mit Passkey anmelden'
      passkeyLoginDisabled={isProcessing}
      // Modal customization
      showNoTokenModal={showNoTokenModal}
      setShowNoTokenModal={setShowNoTokenModal}
      showFirstLoginModal={showFirstLoginModal}
      setShowFirstLoginModal={setShowFirstLoginModal}
      // Error handling
      error={
        error ||
        (isAuthError
          ? 'Benutzername, E-Mail oder Passwort ist falsch. Bitte überprüfe deine Eingabe.'
          : '')
      }
      setError={setError}
      isProcessing={isProcessing}
      setIsProcessing={setIsProcessing}
      // Animation states
      showCheckmarkAnimation={showCheckmarkAnimation}
      setShowCheckmarkAnimation={setShowCheckmarkAnimation}
      showCrossAnimation={showCrossAnimation}
      setShowCrossAnimation={setShowCrossAnimation}
    />
    </Box>
  );
}

export default RfidLogin;
